import * as React from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export const AOSelectorDialog = ({
  open,
  setOpen,
  availableObjectives,
  onSelect,
}) => {
  const [search, setSearch] = React.useState("");
  const [selectedSubject, setSelectedSubject] = React.useState("all");

  // ---- unique subjects ----
  const subjects = React.useMemo(() => {
    const map = new Map();
    availableObjectives.forEach((ao) => {
      map.set(ao.subject.name, ao.subject);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [availableObjectives]);

  // ---- filter + sort ----
  const filteredObjectives = React.useMemo(() => {
    const lower = search.toLowerCase();

    return availableObjectives
      .filter((ao) => {
        const matchesSubject =
          selectedSubject === "all" || ao.subject.name === selectedSubject;

        const matchesSearch =
          ao.identifier.toLowerCase().includes(lower) ||
          ao.subject.name.toLowerCase().includes(lower);

        return matchesSubject && matchesSearch;
      })
      .sort((a, b) => {
        const subjectCompare = a.subject.name.localeCompare(b.subject.name);
        if (subjectCompare !== 0) return subjectCompare;

        return a.identifier.localeCompare(b.identifier);
      });
  }, [availableObjectives, selectedSubject, search]);

  const disableSubjectSelect = subjects.length <= 1;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth={"lg"}
      slotProps={{
        paper: {
          component: "form",
          sx: {
            backgroundImage: "none",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle>Choose an assessment objective</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2 }}>
          <TextField
            label="Search"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by subject or AO code"
          />

          <TextField
            select
            label="Subject"
            size="small"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            disabled={disableSubjectSelect}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All subjects</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.name} value={s.name}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {filteredObjectives.map((ao) => (
          <Card key={ao.id} variant={"background"} sx={{ mb: 2 }}>
            <CardContent>
              <Stack
                direction={"row"}
                spacing={2}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack direction={"column"}>
                  <Stack direction={"row"} spacing={2}>
                    <Typography variant={"subtitle1"} fontStyle={"medium"}>
                      {ao.identifier}
                    </Typography>
                    <Chip label={ao.subject.name} />
                  </Stack>
                  <Typography variant={"subtitle1"} fontStyle={"regular"}>
                    {ao.focusArea}
                  </Typography>
                </Stack>
                <Button variant={"card-action"} onClick={() => onSelect(ao)}>
                  Select
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} variant={"outlined"}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
